import { ApiError } from '@/services/apiFetch'
import type {
  WorkspaceClipboard,
  WorkspaceNode,
  WorkspaceQuota,
  WorkspaceUploadInput,
} from '@/services/workspace'

type ApiErrorBody = {
  error?: { code?: string; message?: string; status?: number; details?: Record<string, unknown> }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

async function throwIfNotOk(res: Response, fallback: string): Promise<void> {
  if (res.ok) return
  const body = (await res.json().catch(() => null)) as ApiErrorBody | null
  const err = body?.error
  throw new ApiError(
    err?.code ?? 'HTTP_ERROR',
    err?.message ?? `${fallback} (${res.status})`,
    err?.status ?? res.status,
    err?.details,
  )
}

function parentQuery(parentId: string | null): string {
  if (parentId == null) return ''
  return `?parentId=${encodeURIComponent(parentId)}`
}

function basePath(supplierId: string, folderSlug: string): string {
  return `/api/supplier-workspace/${encodeURIComponent(supplierId)}/${encodeURIComponent(folderSlug)}`
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl)
  if (!res.ok) throw new Error('Không đọc được nội dung file để tải lên.')
  return res.blob()
}

let clipboard: WorkspaceClipboard | null = null

export const supplierWorkspaceService = {
  async list(
    supplierId: string,
    folderSlug: string,
    parentId: string | null,
  ): Promise<WorkspaceNode[]> {
    const res = await fetch(
      `${basePath(supplierId, folderSlug)}${parentQuery(parentId)}`,
    )
    await throwIfNotOk(res, 'Không tải được danh sách file')
    const body = await parseJson<{ items: WorkspaceNode[] } & Partial<WorkspaceQuota>>(res)
    return body.items ?? []
  },

  async getQuota(): Promise<WorkspaceQuota> {
    const res = await fetch('/api/supplier-workspace/quota')
    await throwIfNotOk(res, 'Không tải được dung lượng lưu trữ')
    return parseJson<WorkspaceQuota>(res)
  },

  async getPath(
    supplierId: string,
    folderSlug: string,
    nodeId: string | null,
  ): Promise<WorkspaceNode[]> {
    const q = nodeId == null ? '' : `?nodeId=${encodeURIComponent(nodeId)}`
    const res = await fetch(`${basePath(supplierId, folderSlug)}/path${q}`)
    await throwIfNotOk(res, 'Không tải được đường dẫn thư mục')
    return parseJson<WorkspaceNode[]>(res)
  },

  async createFolder(
    supplierId: string,
    folderSlug: string,
    parentId: string | null,
    name?: string,
  ): Promise<WorkspaceNode> {
    const res = await fetch(`${basePath(supplierId, folderSlug)}/folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId, name }),
    })
    await throwIfNotOk(res, 'Không tạo được thư mục')
    return parseJson<WorkspaceNode>(res)
  },

  async uploadFiles(
    supplierId: string,
    folderSlug: string,
    parentId: string | null,
    files: WorkspaceUploadInput[],
  ): Promise<WorkspaceNode[]> {
    const form = new FormData()
    if (parentId != null) form.append('parentId', parentId)

    for (const item of files) {
      let blob: Blob
      let filename = item.name
      if (item.file) {
        blob = item.file
        if (item.file instanceof File && item.file.name) {
          filename = item.file.name
        }
      } else if (item.dataUrl) {
        blob = await dataUrlToBlob(item.dataUrl)
      } else {
        throw new ApiError('INVALID_FILE', 'Thiếu nội dung file để tải lên.', 400)
      }
      form.append('files', blob, filename)
    }

    const res = await fetch(`${basePath(supplierId, folderSlug)}/upload`, {
      method: 'POST',
      body: form,
    })
    await throwIfNotOk(res, 'Tải lên thất bại')
    return parseJson<WorkspaceNode[]>(res)
  },

  async rename(
    _supplierId: string,
    _folderSlug: string,
    id: string,
    name: string,
  ): Promise<WorkspaceNode> {
    const res = await fetch(`/api/supplier-workspace/files/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    })
    await throwIfNotOk(res, 'Đổi tên thất bại')
    return parseJson<WorkspaceNode>(res)
  },

  async remove(supplierId: string, folderSlug: string, ids: string[]): Promise<void> {
    const res = await fetch('/api/supplier-workspace/files/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productCode: supplierId, folderSlug, ids }),
    })
    await throwIfNotOk(res, 'Xóa thất bại')
  },

  async move(
    sourceSupplierId: string,
    sourceSlug: string,
    ids: string[],
    targetSupplierId: string,
    targetSlug: string,
    targetParentId: string | null,
  ): Promise<void> {
    const res = await fetch('/api/supplier-workspace/files/move', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceCode: sourceSupplierId,
        sourceSlug,
        ids,
        targetCode: targetSupplierId,
        targetSlug,
        targetParentId,
      }),
    })
    await throwIfNotOk(res, 'Di chuyển thất bại')
  },

  async copy(
    sourceSupplierId: string,
    sourceSlug: string,
    ids: string[],
    targetSupplierId: string,
    targetSlug: string,
    targetParentId: string | null,
  ): Promise<WorkspaceNode[]> {
    const res = await fetch('/api/supplier-workspace/files/copy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceCode: sourceSupplierId,
        sourceSlug,
        ids,
        targetCode: targetSupplierId,
        targetSlug,
        targetParentId,
      }),
    })
    await throwIfNotOk(res, 'Sao chép thất bại')
    return parseJson<WorkspaceNode[]>(res)
  },

  cutToClipboard(supplierId: string, folderSlug: string, ids: string[]): void {
    clipboard = { operation: 'cut', productCode: supplierId, folderSlug, ids: [...ids] }
  },

  copyToClipboard(supplierId: string, folderSlug: string, ids: string[]): void {
    clipboard = { operation: 'copy', productCode: supplierId, folderSlug, ids: [...ids] }
  },

  getClipboard(): WorkspaceClipboard | null {
    return clipboard
  },

  async paste(
    supplierId: string,
    folderSlug: string,
    parentId: string | null,
  ): Promise<void> {
    if (!clipboard || clipboard.ids.length === 0) return
    if (clipboard.operation === 'copy') {
      await this.copy(
        clipboard.productCode,
        clipboard.folderSlug,
        clipboard.ids,
        supplierId,
        folderSlug,
        parentId,
      )
      return
    }
    await this.move(
      clipboard.productCode,
      clipboard.folderSlug,
      clipboard.ids,
      supplierId,
      folderSlug,
      parentId,
    )
    clipboard = null
  },
}
